import { supabaseClient } from './supabase';

// Cache for stop words by language
let stopWordsCacheByLanguage: { [key: string]: Set<string> } = {};
let lastCacheUpdate = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Function to load stop words from database for specific language
async function loadStopWords(language: string = 'uk'): Promise<Set<string>> {
  try {
    const { data, error } = await supabaseClient
      .from('stop_words')
      .select('word')
      .eq('language', language);

    if (error) {
      console.error('Error loading stop words from database:', error);
      return new Set();
    }

    // Create set from database words
    const allStopWords = new Set<string>();
    data?.forEach(({ word }) => allStopWords.add(word.toLowerCase()));

    return allStopWords;
  } catch (error) {
    console.error('Error in loadStopWords:', error);
    return new Set();
  }
}

// Function to get stop words with caching for specific language
async function getStopWords(language: string = 'uk'): Promise<Set<string>> {
  const now = Date.now();

  // Return cached stop words if they're still valid
  if (stopWordsCacheByLanguage[language] && (now - lastCacheUpdate) < CACHE_TTL) {
    return stopWordsCacheByLanguage[language];
  }

  // Load fresh stop words
  const words = await loadStopWords(language);
  stopWordsCacheByLanguage[language] = words;
  lastCacheUpdate = now;

  return words;
}

// Function to check if a word is a stop word
export async function isStopWord(word: string, language: string = 'uk'): Promise<boolean> {
  const stopWords = await getStopWords(language);
  return stopWords.has(word.toLowerCase());
}

// Function to normalize text and remove stop words
export async function normalizeText(text: string, language: string = 'uk'): Promise<string> {
  if (!text || typeof text !== 'string') {
    return '';
  }

  const stopWords = await getStopWords(language);
  // Updated regex to preserve apostrophes and support multiple languages (Ukrainian, Russian, English)
  const words = text
    .toLowerCase()
    .replace(/[^а-яіїєґa-z'\\-\\s]/gi, ' ')
    .split(/\s+/);

  const filteredWords: string[] = [];
  for (const word of words) {
    if (word.length > 2 && !stopWords.has(word)) {
      filteredWords.push(word);
    }
  }

  return filteredWords.join(' ');
}

// Function to filter keywords by stop words
export async function filterKeywords(keywords: string[], language: string = 'uk'): Promise<string[]> {
  const stopWords = await getStopWords(language);
  return keywords.filter(word => !stopWords.has(word.toLowerCase()));
}

// Function to update stop words cache
export function clearStopWordsCache() {
  stopWordsCacheByLanguage = {};
  lastCacheUpdate = 0;
}